import React, { useState, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import TableHeader from './achatsTableHeader';
import SubtleBadge from 'components/common/SubtleBadge';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAchats } from 'data/achats';
import { OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import { FaRegFileLines } from 'react-icons/fa6';
import { RiFileEditLine } from 'react-icons/ri';
import { FiArchive } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const columns = [
  {
    accessorKey: 'idEntete',
    header: 'RÉFÉRENCE',
    meta: {
      headerProps: { className: 'pe-1 white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap' }
    }
  },
  {
    accessorKey: 'date',
    header: 'DATE',
    accessorFn: row => {
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(new Date(row.date));
    },
    meta: {
      headerProps: { className: 'ps-2 text-900', style: { height: '46px' } },
      cellProps: {
        className: 'white-space-nowrap pe-5 ps-2 py-2'
      }
    }
  },
  {
    accessorKey: 'fournisseur',
    header: 'FOURNISSEUR',
    meta: {
      headerProps: { className: 'text-start text-900 ' },
      cellProps: {
        className: 'ps-2 text-end w-5 text-primary fw-semibold'
      }
    },
    //"xs" | "lg" | "sm" | "1x" | "2x" | "3x" | "4x" | "5x" | "6x" | "7x" | "8x" | "9x" | "10x"
    cell: ({ row: { original } }) => {
      const { emailResponsable, societe } = original;
      return (
        <div className="d-flex">
          <FontAwesomeIcon
            icon="user"
            size="lg"
            className="me-3 text-secondary"
          />
          <div>
            {societe && (
              <p
                className="text-start text-secondary"
                style={{ marginBottom: 0 }}
              >
                {societe}
              </p>
            )}
            {emailResponsable && (
              <p
                className="text-start text-secondary"
                style={{ marginBottom: 0 }}
              >
                {emailResponsable}
              </p>
            )}
          </div>
        </div>
      );
    }
  },
  {
    accessorKey: 'TTC',
    header: 'TTC',
    meta: {
      headerProps: { className: 'text-start text-900 ' },
      cellProps: {
        className: 'ps-5 text-end w-1 text-primary fw-semibold'
      }
    },
    cell: ({ row: { original } }) => {
      const { montantHt, ttc } = original;
      return (
        <div>
          {ttc && (
            <div>
              <span className="fs-9" style={{ marginBottom: 0 }}>
                {`${ttc}`}
              </span>
              <sup>TND</sup>
            </div>
          )}
          {montantHt && (
            <div>
              <span className="fs-10 text-danger" style={{ marginBottom: 0 }}>
                {`${montantHt}`}
              </span>
              <sup className="text-danger">TND</sup>
            </div>
          )}
        </div>
      );
    }
  },
  {
    accessorKey: 'STATUT',
    header: 'STATUT',
    meta: {
      headerProps: { className: 'text-start text-900 ' },
      cellProps: {
        className: 'ps-2 text-end w-5 text-primary fw-semibold'
      }
    },
    cell: ({ row: { original } }) => {
      const { statut } = original;

      return (
        <div className="text-center">
          <SubtleBadge
            bg={classNames({
              success: statut === 'créé',
              primary: statut === 'BL',
              warning: statut === 'BS',
              secondary: statut === null
            })}
            className="px-2 py-1" // Add padding for better appearance
            style={{ display: 'inline-block' }} // Ensure it takes only the space of the content
          >
            <p className="mb-0 fs-10">{statut}</p>
          </SubtleBadge>
        </div>
      );
    }
  },
  {
    accessorKey: 'ACTIONS',
    header: 'ACTIONS',
    meta: {
      headerProps: {
        className: 'text-center text-900'
      },
      cellProps: {
        className: 'fs-9'
      }
    },
    cell: ({ row: { original } }) => {
      const { idEntete } = original;

      const navigate = useNavigate();

      return (
        <div className="text-end">
          <OverlayTrigger
            overlay={
              <Tooltip
                style={{ position: 'fixed' }} // Apply the custom style
                className="custom-tooltip" // Apply the custom class
                id="overlay-trigger-example"
              >
                Consulter Produit
              </Tooltip>
            }
          >
            <Button
              variant="link"
              className="p-0"
              onClick={() => {
                navigate(`/dashboard/achat/${idEntete}`);
              }}
            >
              <FaRegFileLines />
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            overlay={
              <Tooltip
                style={{ position: 'fixed' }} // Apply the custom style
                className="custom-tooltip" // Apply the custom class
                id="overlay-trigger-example"
              >
                Editer Produit
              </Tooltip>
            }
          >
            <Button
              variant="link"
              className="p-0 mx-3"
              onClick={() => navigate(`/dashboard/achat/${idEntete}`)}
            >
              <RiFileEditLine />
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            overlay={
              <Tooltip
                style={{ position: 'fixed' }} // Apply the custom style
                className="custom-tooltip" // Apply the custom class
                id="overlay-trigger-example"
              >
                Archiver Produit
              </Tooltip>
            }
          >
            <Button variant="link" className="p-0">
              <FiArchive />
            </Button>
          </OverlayTrigger>
        </div>
      );
    }
  }
];

const AchatsTable = () => {
  const [data, setData] = useState([]);
  useEffect(() => {
    const fetchAchats = async () => {
      const response = await getAchats();
      setData(response);
      console.log('achatsFetched : ', response);
    };
    fetchAchats();
  }, []);

  const table = useAdvanceTable({
    data: data,
    columns: columns,
    selection: true,
    sortable: true,
    pagination: true,
    perPage: 90000,
    selectionColumnWidth: 52
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <Card>
          <Card.Header className="px-0">
            <TableHeader
            // handleShow={handleShow}
            // handleDetailsUpdate={handleDetailsUpdate}
            // setTypeLignes={setTypeLignes}
            />
          </Card.Header>
          <Card.Body
            className="p-0"
            style={{ height: '500px', overflowY: 'auto' }}
          >
            <AdvanceTable
              headerClassName="bg-body-tertiary align-middle"
              rowClassName="btn-reveal-trigger align-middle"
              tableProps={{
                size: 'sm',
                className: 'fs-10 mb-0 overflow-hidden'
              }}
              // onRowClick={row => handleLignes(row)}
              fixedHeader={true}
            />
          </Card.Body>
          <Card.Footer>
            <AdvanceTableFooter rowInfo />
          </Card.Footer>
        </Card>
      </AdvanceTableProvider>
    </>
  );
};

export default AchatsTable;
